import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "./Shared";
import { useLenis } from "../../context";

const PRIVACY = {
    slug: "polityka-prywatnosci",
    eyebrow: "Informacje prawne",
    title: "Polityka prywatności",
    metaTitle: "Polityka prywatności | Tilio — domki nad Liwcem",
    metaDesc:
        "Polityka prywatności TILIO — zasady przetwarzania danych osobowych zgodnie z RODO.",
    canonical: "https://tilio.eu/polityka-prywatnosci",
    blocks: [
        {
            type: "paragraph",
            text: "Administratorem danych osobowych jest TILIO. Dane przekazane za pośrednictwem strony, w tym podczas kontaktu lub rezerwacji, są przetwarzane wyłącznie w celu obsługi zapytania, rezerwacji oraz realizacji usług.",
        },
        {
            type: "paragraph",
            text: "Dane są przetwarzane zgodnie z obowiązującymi przepisami, w szczególności RODO. Użytkownik ma prawo dostępu do swoich danych, ich poprawiania, usunięcia oraz ograniczenia przetwarzania.",
        },
        {
            type: "paragraph",
            text: "W sprawach dotyczących danych osobowych można skontaktować się z nami poprzez adres: kontakt@tilio.eu",
        },
    ],
};

const TERMS = {
    slug: "regulamin",
    eyebrow: "Informacje prawne",
    title: "Regulamin rezerwacji i pobytu w TILIO",
    metaTitle: "Regulamin rezerwacji i pobytu | Tilio — domki nad Liwcem",
    metaDesc:
        "Regulamin rezerwacji i pobytu w TILIO — zasady rezerwacji, płatności, anulowania oraz pobytu w domkach nad Liwcem.",
    canonical: "https://tilio.eu/regulamin",
    blocks: [
        {
            type: "section",
            number: "1",
            heading: "Rezerwacja",
            items: [
                "Rezerwacji pobytu można dokonać za pośrednictwem systemu rezerwacyjnego dostępnego na stronie TILIO.",
                "Po dokonaniu rezerwacji Gość otrzymuje potwierdzenie na podany adres e-mail.",
                "Rezerwacja zostaje potwierdzona po dokonaniu wymaganej płatności.",
                "Przed dokonaniem rezerwacji Gość otrzymuje informację o terminie pobytu, liczbie osób, całkowitej cenie, sposobie płatności oraz zasadach anulowania.",
                "W przypadku rezerwacji dokonanej za pośrednictwem Airbnb lub Slowhop obowiązują zasady dotyczące danej rezerwacji określone przez odpowiednią platformę.",
            ],
        },
        {
            type: "section",
            number: "2",
            heading: "Płatność",
            items: [
                "Cena pobytu oraz dostępne metody i terminy płatności są przedstawione podczas dokonywania rezerwacji.",
                "Dokonanie płatności w wymaganym terminie jest warunkiem potwierdzenia rezerwacji.",
                "Wszystkie dodatkowe opłaty, jeżeli występują, są wskazane przed dokonaniem rezerwacji.",
            ],
        },
        {
            type: "section",
            number: "3",
            heading: "Anulowanie rezerwacji",
            items: [
                "Gość może anulować rezerwację, zgłaszając rezygnację poprzez system rezerwacyjny lub kontaktując się z TILIO pod adresem kontakt@tilio.eu.",
                "Za moment anulowania rezerwacji uznaje się moment otrzymania zgłoszenia przez TILIO.",
            ],
            subheading: "Zasady zwrotu:",
            bullets: [
                "14 dni lub więcej przed przyjazdem – zwrot 100% wpłaconej kwoty,",
                "7–13 dni przed przyjazdem – zwrot 50% wpłaconej kwoty,",
                "mniej niż 7 dni przed przyjazdem – brak zwrotu.",
            ],
            itemsAfter: [
                "Zwrot środków, jeżeli przysługuje, zostanie dokonany przy użyciu metody płatności wykorzystanej przy rezerwacji.",
                "Powyższe zasady dotyczą rezerwacji dokonanych bezpośrednio w TILIO. W przypadku rezerwacji przez Airbnb lub Slowhop obowiązują zasady właściwe dla danej platformy.",
            ],
        },
        {
            type: "section",
            number: "4",
            heading: "Zmiana terminu pobytu",
            items: [
                "Zmiana terminu pobytu jest możliwa po wcześniejszym uzgodnieniu z TILIO i zależy od dostępności obiektu.",
                "Zmiana terminu nie jest gwarantowana.",
                "Jeżeli cena nowego terminu różni się od ceny pierwotnej rezerwacji, cena pobytu może ulec zmianie.",
            ],
        },
        {
            type: "section",
            number: "5",
            heading: "Skrócenie pobytu",
            items: [
                "Skrócenie pobytu po jego rozpoczęciu nie uprawnia do zwrotu kosztów za niewykorzystane noce.",
            ],
        },
        {
            type: "section",
            number: "6",
            heading: "Brak przyjazdu (no-show)",
            items: [
                "W przypadku braku przyjazdu bez wcześniejszego anulowania rezerwacji wpłacona kwota nie podlega zwrotowi.",
            ],
        },
        {
            type: "section",
            number: "7",
            heading: "Pobyt w TILIO",
            items: [
                "Gość zobowiązuje się korzystać z obiektu zgodnie z jego przeznaczeniem, zasadami bezpieczeństwa oraz niniejszym Regulaminem.",
                "W obiekcie mogą przebywać wyłącznie osoby objęte rezerwacją lub wcześniej uzgodnione z TILIO.",
                "Gość zobowiązuje się do poszanowania obiektu, jego wyposażenia oraz otoczenia.",
            ],
        },
        {
            type: "section",
            number: "8",
            heading: "Zameldowanie i wymeldowanie",
            items: [
                "Doba pobytowa rozpoczyna się o godzinie 15:00 w dniu przyjazdu i kończy o godzinie 11:00 w dniu wyjazdu.",
                "Przyjazd lub wyjazd poza ustalonymi godzinami wymaga wcześniejszego uzgodnienia z TILIO.",
                "Szczegółowe informacje dotyczące zameldowania i dostępu do obiektu są przekazywane Gościowi przed przyjazdem.",
            ],
        },
        {
            type: "section",
            number: "9",
            heading: "Odpowiedzialność za szkody",
            items: [
                "Gość odpowiada za szkody wyrządzone w obiekcie lub jego wyposażeniu przez siebie oraz osoby, za które odpowiada.",
                "O powstałej szkodzie lub awarii należy poinformować TILIO niezwłocznie.",
                "Gość nie odpowiada za normalne zużycie wyposażenia wynikające z prawidłowego korzystania z obiektu.",
            ],
        },
        {
            type: "section",
            number: "10",
            heading: "Anulowanie rezerwacji przez TILIO",
            items: [
                "Jeżeli z przyczyn leżących po stronie TILIO realizacja pobytu nie będzie możliwa, Gość zostanie o tym poinformowany niezwłocznie.",
                "W takim przypadku Gość otrzyma zwrot wpłaconych środków dotyczących niezrealizowanego pobytu.",
                "W miarę możliwości TILIO może zaproponować zmianę terminu pobytu.",
            ],
        },
        {
            type: "section",
            number: "11",
            heading: "Reklamacje",
            items: [
                "W przypadku problemu podczas pobytu Gość powinien poinformować TILIO niezwłocznie, aby umożliwić jego rozwiązanie.",
                "Reklamacje można zgłaszać na adres kontakt@tilio.eu.",
                "Reklamacje są rozpatrywane zgodnie z obowiązującymi przepisami prawa.",
            ],
        },
        {
            type: "section",
            number: "12",
            heading: "Prawo odstąpienia od umowy",
            items: [
                "Rezerwacja pobytu w TILIO dotyczy zakwaterowania w oznaczonym terminie.",
                "W przypadku takiej umowy konsumentowi nie przysługuje ustawowe prawo odstąpienia od umowy zawartej na odległość na zasadach przewidzianych dla innych umów zawieranych online.",
                "Anulowanie rezerwacji odbywa się na zasadach określonych w niniejszym Regulaminie, z uwzględnieniem praw wynikających z bezwzględnie obowiązujących przepisów prawa.",
            ],
        },
        {
            type: "section",
            number: "13",
            heading: "Dane osobowe",
            items: [
                "Dane osobowe Gościa są przetwarzane w celu obsługi rezerwacji, realizacji pobytu, kontaktu z Gościem oraz realizacji obowiązków prawnych.",
                "Szczegółowe informacje dotyczące przetwarzania danych osobowych znajdują się w Polityce prywatności dostępnej na stronie TILIO.",
            ],
        },
        {
            type: "section",
            number: "14",
            heading: "Postanowienia końcowe",
            items: [
                "W sprawach nieuregulowanych w Regulaminie zastosowanie mają przepisy prawa polskiego.",
                "Żadne postanowienie Regulaminu nie ogranicza praw konsumenta wynikających z bezwzględnie obowiązujących przepisów prawa.",
                "Aktualna wersja Regulaminu jest dostępna na stronie internetowej TILIO.",
            ],
        },
    ],
};

const DOCS = {
    "polityka-prywatnosci": PRIVACY,
    regulamin: TERMS,
};

function Section({ block }) {
    return (
        <div className="border-b border-stone/70 py-8" data-testid={`legal-section-${block.number}`}>
            <h2 className="font-serif text-2xl tracking-tight text-pine md:text-[1.7rem]">
                <span className="mr-3 text-sage/70">{block.number}.</span>
                {block.heading}
            </h2>
            <div className="mt-4 space-y-3">
                {block.items?.map((t, i) => (
                    <p key={`i-${i}`} className="text-[17px] leading-[1.65] text-sage">
                        {t}
                    </p>
                ))}
            </div>
            {block.subheading && (
                <p className="mt-5 text-[17px] font-medium leading-[1.65] text-pine">{block.subheading}</p>
            )}
            {block.bullets && (
                <ul className="mt-3 space-y-2">
                    {block.bullets.map((b, i) => (
                        <li key={`b-${i}`} className="flex gap-3 text-[17px] leading-[1.65] text-sage">
                            <span className="mt-[2px] text-sage/60">•</span>
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            )}
            {block.itemsAfter && (
                <div className="mt-4 space-y-3">
                    {block.itemsAfter.map((t, i) => (
                        <p key={`a-${i}`} className="text-[17px] leading-[1.65] text-sage">
                            {t}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function LegalPage({ docKey }) {
    const doc = DOCS[docKey] || PRIVACY;
    const lenis = useLenis();
    const location = useLocation();

    useEffect(() => {
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [location.pathname, lenis]);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = doc.metaTitle;
        const canonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonical?.getAttribute("href");
        if (canonical) canonical.setAttribute("href", doc.canonical);
        const desc = document.querySelector('meta[name="description"]');
        const prevDesc = desc?.getAttribute("content");
        if (desc) desc.setAttribute("content", doc.metaDesc);
        return () => {
            document.title = prevTitle;
            if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
            if (desc && prevDesc) desc.setAttribute("content", prevDesc);
        };
    }, [doc]);

    return (
        <section data-testid={`legal-page-${doc.slug}`} className="px-6 pt-40 pb-24 md:pt-48 lg:pb-32">
            <div className="mx-auto max-w-3xl">
                <Reveal>
                    <p className="overline-label" data-testid="legal-eyebrow">{doc.eyebrow}</p>
                    <h1
                        className="mt-5 font-serif text-[clamp(2.2rem,1.5rem+2vw,3.4rem)] leading-[1.1] tracking-[-1px] text-pine"
                        data-testid="legal-title"
                    >
                        {doc.title}
                    </h1>
                </Reveal>

                <Reveal delay={0.1} className="mt-12">
                    {doc.blocks[0].type === "paragraph" ? (
                        <div className="space-y-5">
                            {doc.blocks.map((b, i) => (
                                <p key={i} className="text-[17px] leading-[1.7] text-sage md:text-lg">
                                    {b.text}
                                </p>
                            ))}
                        </div>
                    ) : (
                        <div className="border-t border-stone">
                            {doc.blocks.map((b) => (
                                <Section key={b.number} block={b} />
                            ))}
                        </div>
                    )}
                </Reveal>

                <Reveal delay={0.15} className="mt-14">
                    <Link
                        data-testid="legal-back-link"
                        to="/"
                        className="link-underline inline-flex items-center gap-2 text-base text-sage transition-colors duration-300 hover:text-pine"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.25} />
                        Wróć do strony głównej
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}
